import { useCallback, useEffect, useState } from 'react';
import { useLocaleStore } from '@/lib/core/stores';
import { syntheticGuid } from '@/lib/core/utils';
import { deletePreference, getUserPreferences, setPreference } from '@/services/platform/data/preferences';

/** Shared bucket for every event-category "interest" — not a real CMS node, see hook docstring. */
const EVENT_CATEGORY_BUCKET = syntheticGuid('event-category-bucket');
const EVENT_CATEGORY_LABEL = 'Event Categories';

export interface UseEventCategoryInterestResult {
  isSelected: boolean;
  isLoading: boolean;
  toggle: () => Promise<void>;
}

/**
 * Toggles the event's `eventCategory` (a free-text Salesforce value, e.g. "DJ") as a user
 * "interest", reusing the existing Preferences write path (`setPreference`/`deletePreference`).
 * Not backed by real CMS content — `subcategoryNodeKey` is a deterministic synthetic Guid
 * derived from the category string (see `syntheticGuid`), so this "interest" round-trips
 * through storage but won't appear in "Your Interests" or the home Interests module (both
 * walk the real CMS preference tree) — EDP-only for now.
 */
export function useEventCategoryInterest(
  category: string | null | undefined
): UseEventCategoryInterestResult {
  const locale = useLocaleStore(state => state.locale);
  const [preferenceGuid, setPreferenceGuid] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(!!category);

  const subcategoryNodeKey = category ? syntheticGuid(category) : null;

  const refetch = useCallback(async () => {
    if (!category || !subcategoryNodeKey) {
      setPreferenceGuid(null);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    const preferences = await getUserPreferences(locale);
    const match = (preferences ?? []).find(p => p.subcategoryNodeKey === subcategoryNodeKey);
    setPreferenceGuid(match?.publicGuid ?? null);
    setIsLoading(false);
  }, [category, subcategoryNodeKey, locale]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  const toggle = useCallback(async () => {
    if (!category || !subcategoryNodeKey) return;
    if (preferenceGuid) {
      await deletePreference(preferenceGuid, locale);
    } else {
      await setPreference(
        {
          categoryNodeKey: EVENT_CATEGORY_BUCKET,
          category: EVENT_CATEGORY_LABEL,
          subcategoryNodeKey,
          subcategory: category,
          value: category,
        },
        locale
      );
    }
    await refetch();
  }, [category, subcategoryNodeKey, preferenceGuid, locale, refetch]);

  return { isSelected: !!preferenceGuid, isLoading, toggle };
}
